import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useState } from 'react'
import { ApiError } from '../lib/api.js'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { listFolders } from '../lib/folders-api.js'
import { createProject } from '../lib/projects-api.js'
import { useToast } from '../lib/toast.js'
import { ModalOverlay } from './ModalOverlay.js'

type Props = {
  open: boolean
  onClose: () => void
  defaultFolderId?: string | null
}

export function CreateProjectDialog({ open, onClose, defaultFolderId = null }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState('')
  const [folderId, setFolderId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const foldersQ = useQuery({
    queryKey: ['folders'],
    queryFn: listFolders,
    enabled: open,
  })

  useEffect(() => {
    if (open) {
      setFolderId(defaultFolderId ?? '')
      setError(null)
    }
  }, [open, defaultFolderId])

  const mutation = useMutation({
    mutationFn: (input: { name: string; folderId?: string | null }) => createProject(input),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['folders'] })
      toast.success(`プロジェクト「${project.name}」を作成しました`)
      setName('')
      setFolderId('')
      setError(null)
      onClose()
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'PLAN_LIMIT_EXCEEDED') {
          setError('プラン上限に達しました。有料プランへのアップグレードをご検討ください。')
        } else {
          setError(err.message)
        }
      } else {
        setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
      }
    },
  })

  if (!open) return null

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('プロジェクト名を入力してください')
      return
    }
    mutation.mutate({
      name: name.trim(),
      folderId: folderId === '' ? null : folderId,
    })
  }

  return (
    <ModalOverlay className="bg-black/50">
      <div className="bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold">新規プロジェクト作成</h2>
        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div>
            <label htmlFor="project-name" className="block text-sm font-medium mb-1">
              プロジェクト名
            </label>
            <input
              id="project-name"
              type="text"
              // biome-ignore lint/a11y/noAutofocus: モーダル内のメイン入力なので OK
              autoFocus
              required
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
              placeholder="例: 撮影プロジェクト 2026春"
            />
          </div>
          <div>
            <label htmlFor="project-folder" className="block text-sm font-medium mb-1">
              フォルダ（任意）
            </label>
            <select
              id="project-folder"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
              disabled={foldersQ.isLoading}
            >
              <option value="">未分類</option>
              {(foldersQ.data?.items ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="px-4 py-2 border border-border rounded-md font-medium"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-foreground text-background rounded-md font-medium disabled:opacity-50"
            >
              {mutation.isPending ? '作成中…' : '作成'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}
